import React from "react";
import { Box, Typography, Stepper, Step, StepLabel, StepConnector, stepConnectorClasses, styled } from "@mui/material";
import { formatDate } from "../utils/formatDate";

import {
    Flag as FlagIcon,
    Architecture as ArchitectureIcon,
    CheckCircle as CheckCircleIcon,
    Psychology as PsychologyIcon,
    Lightbulb as LightbulbIcon,
    Engineering as EngineeringIcon,
    Build as BuildIcon,
    AccessTime as AccessTimeIcon,
    Rule as RuleIcon,
    Payments as PaymentsIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Money as MoneyIcon,
    TrendingUpSharp as TrendingUpSharpIcon,
    AccountBalanceWallet as AccountBalanceWalletIcon,
    Warning as WarningIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    TrendingUp as TrendingUpIcon,
    SyncProblem as SyncProblemIcon,
    Sync as SyncIcon,
    MoreHoriz as MoreHorizIcon,
    AssignmentTurnedIn as AssignmentTurnedInIcon,
} from "@mui/icons-material";

interface GetIconProps {
    type: string;
    color?: string;
    fontSize?: number | "small" | "medium" | "large";
}

const ICON_MAP: Record<string, React.ElementType> = {
    Flag: FlagIcon,
    Architecture: ArchitectureIcon,
    CheckCircle: CheckCircleIcon,
    Psychology: PsychologyIcon,
    Lightbulb: LightbulbIcon,
    Engineering: EngineeringIcon,
    Build: BuildIcon,
    AccessTime: AccessTimeIcon,
    Rule: RuleIcon,
    Payments: PaymentsIcon,
    Edit: EditIcon,
    Delete: DeleteIcon,
    Money: MoneyIcon,
    TrendingUpSharp: TrendingUpSharpIcon,
    AccountBalanceWallet: AccountBalanceWalletIcon,
    Warning: WarningIcon,
    Error: ErrorIcon,
    Info: InfoIcon,
    TrendingUp: TrendingUpIcon,
    SyncProblem: SyncProblemIcon,
    Sync: SyncIcon,
    MoreHoriz: MoreHorizIcon,
    AssignmentTurnedIn: AssignmentTurnedInIcon,
};

export const GetIcon: React.FC<GetIconProps> = ({
    type,
    color = "primary",
    fontSize = "medium",
}) => {
    const IconComponent = ICON_MAP[type] || AssignmentTurnedInIcon;
    return <IconComponent sx={{ color, fontSize }} />;
};

const ColorConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 22,
    },
    [`& .${stepConnectorClasses.line}`]: {
        height: 3,
        border: 0,
        backgroundColor: theme.palette.divider,
        borderRadius: 1,
    },
    [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
        backgroundColor: theme.palette.success.main,
    },
    [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
        backgroundColor: theme.palette.success.main,
    },
}));

const TimelineIcon = styled("div")<{
    progress: number;
    status: string;
    color: 'green' | 'amber' | 'red'
}>(({ theme, progress, status, color }) => {

    let iconColor = theme.palette.text.secondary;
    if (color === 'green') {
        iconColor = theme.palette.success.main;
    } else if (color === 'amber') {
        iconColor = theme.palette.warning.main;
    } else if (color === 'red') {
        iconColor = theme.palette.error.main;
    }
    const pct = Math.max(0, Math.min(progress, 100));

    return {
        position: "relative",
        width: 44,
        height: 44,
        borderRadius: "50%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        color: iconColor,
        zIndex: 1,
        backgroundColor: theme.palette.background.paper,
        "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            background: `conic-gradient(${iconColor} ${pct * 3.6}deg, ${theme.palette.divider} ${pct * 3.6}deg)`,
            mask: "radial-gradient(farthest-side, transparent 85%, black 0)",
            WebkitMask: "radial-gradient(farthest-side, transparent 85%, black 0)",
            zIndex: 0,
        },
    };
});

export const ProjectTimeline: React.FC<{ steps: { percentage_complete: string, overall: 'green' | 'amber' | 'red', name: string, status: string, planned_start_date: string, phase_sequence_order: number }[] }> = ({ steps }) => {
    return (
        <Box
            sx={{
                px: 2,
                py: 4,
                borderRadius: 2,
            }}
        >
            <Stepper alternativeLabel connector={<ColorConnector />} activeStep={steps.filter((value) => {
                return value.status === 'completed'
            }).length - 1}>
                {steps.sort((a, b) => {
                    return a.phase_sequence_order - b.phase_sequence_order
                }).map((step, index) => (
                    <Step key={index}>
                        <StepLabel
                            StepIconComponent={() => (
                                <TimelineIcon
                                    progress={Number(step.percentage_complete)}
                                    status={step.status}
                                    color={step.overall}
                                >
                                    <GetIcon type={step.name} />
                                </TimelineIcon>
                            )}
                        >
                            <Typography
                                variant="body2"
                                fontWeight={500}
                                color={
                                    step.overall === 'green' ? 'success.main' :
                                        step.overall === 'amber' ? 'warning.main' :
                                            step.overall === 'red' ? 'text.secondary' : 'text.secondary'
                                }
                            >
                                {step.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {formatDate(step.planned_start_date)}
                            </Typography>
                        </StepLabel>
                    </Step>
                ))}
            </Stepper>
        </Box>
    );
};